/**
 * Google Calendar JSON in, a day's worth of rows out. No DOM and no network,
 * so the rules are testable — the same split agenda.js explains and for the
 * same reason.
 *
 * Two facts about Google's shape drive everything here.
 *
 * 1. **A timed event is an instant, and the student is not in UTC.** Google
 *    sends `dateTime` as an RFC3339 instant, usually written with a `Z`. Read
 *    the hour out of that string and a 09:00 study block appears at 16:00,
 *    which is exactly what the desktop app did on 2026-08-15. Every hour on
 *    this screen comes from a `Date` rendered in the browser's own zone.
 *
 * 2. **An all-day event is a calendar day, and has no instant at all.** Google
 *    sends `date` instead of `dateTime`. `new Date('2026-09-03')` is midnight
 *    *UTC*, which is 5pm on the 2nd in Pacific — so a holiday would land on
 *    the day before. A `date` is split on its own hyphens and never parsed as
 *    a moment in time. And an all-day event gets no hour on screen, because it
 *    does not have one.
 *
 * Nothing here narrows the day. A 06:00 flight and a 23:00 finish are ordinary
 * days, so there is no working-hours grid to fall outside of.
 */
import { isoDay, addDays, dayLabel } from './agenda.js';

const SPAN = { day: 1, week: 7 };
const UNTITLED = 'Untitled event';

// A multi-day event is walked day by day. The cap is a guard against a
// malformed `end.date` centuries away, not a rule about how long a holiday
// may be — a fortnight abroad renders in full.
const MAX_SPAN_DAYS = 400;

/** A local `YYYY-MM-DD` back into a local midnight, without going via UTC. */
function dayFromIso(iso) {
  const [year, month, day] = String(iso).split('-').map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
}

/**
 * The days on screen, and the instants to ask Google for.
 *
 * Rolling rather than calendar, the same choice agenda.js makes: a week on a
 * Thursday is the next seven days, because Monday's lecture matters more than
 * whether Monday shares a calendar week with today.
 *
 * `timeMin`/`timeMax` are local midnight to local midnight expressed as
 * instants. Sending `00:00Z` would fetch somebody else's day.
 */
export function windowFor(view, now) {
  const span = SPAN[view] ?? SPAN.day;
  const first = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const days = [];
  for (let offset = 0; offset < span; offset += 1) days.push(isoDay(addDays(first, offset)));
  return {
    view: SPAN[view] ? view : 'day',
    days,
    timeMin: first.toISOString(),
    timeMax: addDays(first, span).toISOString(),
  };
}

/**
 * One Google event plus the calendar it came from, or `null`.
 *
 * `null` covers a cancelled event, an event with no start, and a start that
 * does not parse — dropped rather than guessed at, the way agenda.js drops a
 * malformed due date.
 */
/**
 * Google's `event` colour palette, by the id an event carries.
 *
 * Alter colours its own events and cannot colour its own CALENDARS: a
 * calendar's colour lives on the calendarList entry, and Google answers 401
 * to writes there under the `calendar.app.created` scope this system holds
 * (see `alter/study_calendar.py`). So the event colour is the only signal
 * that exists, and colouring rows by the calendar threw it away -- study
 * blocks, classes and personal events all arrived looking the same.
 *
 * Hardcoded rather than fetched from `/colors`: these ids are stable, and a
 * request per calendar load to learn eleven constants is a round trip to be
 * told what has not changed since 2011. An id not in this table falls back to
 * the calendar's colour rather than to nothing.
 */
const EVENT_COLOURS = {
  1: '#7986CB',  // Lavender
  2: '#33B679',  // Sage
  3: '#8E24AA',  // Grape
  4: '#E67C73',  // Flamingo
  5: '#F6BF26',  // Banana
  6: '#F4511E',  // Tangerine -- Alter's classes
  7: '#039BE5',  // Peacock
  8: '#616161',  // Graphite
  9: '#3F51B5',  // Blueberry -- Alter's study blocks
  10: '#0B8043', // Basil
  11: '#D50000', // Tomato
};

export function shapeEvent(raw, calendar = {}) {
  if (!raw || raw.status === 'cancelled') return null;

  const start = raw.start ?? {};
  const end = raw.end ?? {};
  const base = {
    id: raw.id ?? null,
    title: typeof raw.summary === 'string' && raw.summary.trim() ? raw.summary : UNTITLED,
    location: typeof raw.location === 'string' ? raw.location : '',
    calendarId: calendar.id ?? null,
    calendarName: calendar.name || calendar.id || '',
    // The event's own colour wins; the calendar's is the fallback for events
    // that carry none, which is every personal one. That fallback is the
    // student's own choice of colour and is left alone.
    colour: EVENT_COLOURS[raw.colorId] || calendar.colour || null,
    // Kept raw as well, because "is this a study block?" is answered by the
    // id rather than by a hex string that two palettes could share.
    colourId: raw.colorId ? String(raw.colorId) : null,
  };

  if (start.date) {
    const startDay = String(start.date);
    if (!dayFromIso(startDay)) return null;
    // Google's `end.date` is exclusive: a single day on the 3rd ends on the
    // 4th. The last day the event actually covers is the day before that.
    const lastDay = end.date ? dayFromIso(end.date) : null;
    const endDay = lastDay ? isoDay(addDays(lastDay, -1)) : startDay;
    return {
      ...base,
      allDay: true,
      start: null,
      end: null,
      startDay,
      endDay: endDay < startDay ? startDay : endDay,
    };
  }

  if (!start.dateTime) return null;
  const startAt = new Date(start.dateTime);
  if (Number.isNaN(startAt.getTime())) return null;
  const endAt = end.dateTime ? new Date(end.dateTime) : null;

  return {
    ...base,
    allDay: false,
    start: startAt,
    end: endAt && !Number.isNaN(endAt.getTime()) ? endAt : null,
    // Filed under the local day it begins on. An event that runs past midnight
    // belongs to the evening being lived, not to the calendar date it ends in.
    startDay: isoDay(startAt),
    endDay: isoDay(startAt),
  };
}

/** The browser's own zone, whatever that is. Never the string Google sent. */
function clock(date) {
  return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
}

export function startLabel(event) {
  if (!event) return '';
  if (event.allDay) return 'All day';
  return event.start ? clock(event.start) : '';
}

/** '' when there is nothing to say, rather than a finish time invented for it. */
export function endLabel(event) {
  if (!event || event.allDay || !event.end) return '';
  return clock(event.end);
}

/** Every local day an event sits on, inside its own span. */
function daysOf(event) {
  if (!event.allDay) return [event.startDay];
  const days = [];
  let cursor = dayFromIso(event.startDay);
  for (let step = 0; cursor && step < MAX_SPAN_DAYS; step += 1) {
    const iso = isoDay(cursor);
    days.push(iso);
    if (iso >= event.endDay) break;
    cursor = addDays(cursor, 1);
  }
  return days;
}

/** All-day first — it frames the day — then in clock order, then by name. */
function compare(a, b) {
  if (a.allDay !== b.allDay) return a.allDay ? -1 : 1;
  if (!a.allDay && !b.allDay) {
    const gap = a.start.getTime() - b.start.getTime();
    if (gap !== 0) return gap;
  }
  return a.title.localeCompare(b.title);
}

/**
 * Which event is the one happening now, or nearest to now.
 *
 * Allen, 2026-08-19: "on alter web it should highlight or glow a calendar
 * event in which the time is closest to so if I have a study block at 1 pm and
 * its 1:20 pm that one should be highlighted."
 *
 * Two rules in that sentence and the second is the one worth getting right.
 * An event you are *inside* wins outright -- at 1:20 the one o'clock block is
 * not merely nearest, it is what you are doing. Only when nothing contains
 * `now` does distance decide, and then it is distance to the nearest edge, so
 * a block that finished five minutes ago beats one starting in an hour.
 *
 * All-day events never win. They contain every instant of their day, so they
 * would contain `now` on every day they cover and the highlight would sit on a
 * birthday rather than on the lecture starting in ten minutes.
 *
 * Nothing at all on a day that is not today: "closest to now" on a Thursday
 * you are looking at on Monday means the first event of Thursday, which is
 * not a useful thing to glow.
 *
 * Returns the event's id, or '' -- an id rather than the object, so the caller
 * comparing it against a row cannot be caught out by a copy.
 */
export function nowEventId(days, now) {
  const today = isoDay(now);
  const at = now.getTime();
  let best = '';
  let bestGap = Infinity;

  for (const day of days ?? []) {
    if (day?.iso !== today) continue;
    for (const event of day.items ?? []) {
      if (!event || event.allDay || !event.start) continue;
      const from = event.start.getTime();
      const to = event.end ? event.end.getTime() : from;
      // Half-open, like every other interval in this system: an event that
      // ends exactly now is over.
      const gap = at >= from && at < to ? 0
        : at < from ? from - at
          : at - to;
      if (gap < bestGap) {
        bestGap = gap;
        best = event.id || '';
      }
    }
  }
  return best;
}


/**
 * Every event, minus whatever calendar the student has switched off.
 *
 * Allen, 2026-09-03: "allow the user to click on a calendar to hide it from
 * the view like for example clicking the Family calendar will hide all
 * events from that on the calendar." A calendar is identified by
 * `calendarId`, which every shaped event already carries -- there is no
 * second definition of "which calendar is this" to keep in step with
 * Google's, and no data-layer change was needed to add this.
 *
 * Called before grouping rather than after: a hidden calendar's events
 * should not count toward a day's "3 events" either.
 *
 * The original array comes back untouched when nothing is hidden, rather
 * than a copy of it -- the common case, and it means "is anything hidden"
 * is never a question the caller has to ask separately.
 *
 * `event?.calendarId ?? ''` rather than the bare id: `screens.js` stores a
 * calendar's id on its chip's `dataset.calendarId`, and a DOM dataset cannot
 * hold `null` -- a calendar with none gets `''` there, while `shapeEvent`
 * above gives its events `calendarId: null`. Unreachable with a real Google
 * id, which is always present, but the two sentinels have to agree, or a
 * calendar with no id could never actually be hidden.
 */
export function visibleEvents(events, hidden) {
  if (!hidden || hidden.size === 0) return events;
  return events.filter((event) => !hidden.has(event?.calendarId ?? ''));
}

/**
 * One entry per day of the window, in order, empty days included.
 *
 * An empty Thursday is a fact about Thursday and worth showing; leaving it out
 * would silently rewrite a week into a shorter one. An event outside the
 * window is dropped rather than given a day of its own.
 */
export function groupEvents(events, window, now) {
  const byDay = new Map(window.days.map((iso) => [iso, []]));

  for (const event of events) {
    if (!event) continue;
    for (const iso of daysOf(event)) {
      byDay.get(iso)?.push(event);
    }
  }

  return window.days.map((iso) => ({
    iso,
    label: dayLabel(iso, now),
    items: byDay.get(iso).sort(compare),
  }));
}
